// Homepage types start
// Homepage specific types that build upon our existing types.ts

// For featured and regular product display and the ProductDetail Page
import {getGroupVideoToken} from "@/app/lib/messagesUtils";
import {GuideBadgeKey} from "@/app/config/badges";

export interface ProductDisplay {
    id: string;
    name: string;
    description: string;
    duration: number;
    teaserImage: string;
    featured: boolean;
    categoryIds: string[];
    categories: CategoryInfo[];
    guide: GuideInfo;
    sessionType: SessionType;
    type: ProductType;
    price: number;
}

export interface GuideInfo {
    id: string;
    givenName: string;
    familyName: string;
    profileImage: string;
    badges?: GuideBadgeKey[];
}

export interface BaseProductDisplay {
    id: string;
    name: string;
    description: string;
    duration: number;
    teaserImage: string;
    featured: boolean;
    categoryIds: string[];
    categories: CategoryInfo[];
    guide: {
        id: string;
        givenName: string;
        familyName: string;
        profileImage: string;
        badges?: GuideBadgeKey[];
    };
    sessionType: SessionType;  // Using the existing SessionType type
}

// Individual product interface
export interface IndividualProductDisplay extends BaseProductDisplay {
    type: ProductType;
    price: number;
}

// Group product interface
export interface GroupProductDisplay extends BaseProductDisplay {
    type: 'VIDEOCALL';
    schedules: EventSchedule[];
}


export type UnifiedProductDisplay = IndividualProductDisplay | GroupProductDisplay;

export function isGroupProduct(product: UnifiedProductDisplay): product is GroupProductDisplay {
    return product.sessionType === 'GROUP';
}


// Types for the category data
export interface CategoryInfo {
    id: string;
    name: string;
    description?: string;  // optional as per DTO
    seoText?: string;     // optional as per DTO
    createdAt?: string;   // LocalDateTime becomes string in JSON
    updatedAt?: string;   // LocalDateTime becomes string in JSON
}


export type EventSchedule = {
    id?: string;
    startTime: string;  // ISO string
    endTime: string;    // ISO string
    duration:number;
    maxParticipants: number;
    pricePerSeat: number;
    currentParticipantCount?: number;
    isCurrentUserParticipant?: boolean;
};

// For category navigation and showcase
export interface CategorySummary {
    id: string
    name: string
    description: string
    imageUrl?: string    // for category showcase
    productCount: number // for badges/filters
    seoText?: string
}

// For paginated product responses
export interface ProductsResponse {
    items: UnifiedProductDisplay[]
    total: number
    page: number
    hasMore: boolean
}

// For the horizontal category bar
export interface CategoryPill {
    id: string
    name: string
    count: number
}

// Homepage types end


export interface BookingGuide {
    id: string;
    given_name: string;
    fullName: string;
    picture?: string;
}

export type StateResetPassword = {
    errors?: {
        email?: string[];
    };
    message?: string | null;
};


export interface TeaserImage {
    id: string           // Changed from _id
    name: string
    imageUrl: string     // Changed from image_url
    categoryIds: string[] // Changed from category_ids
    uploadedAt: string
    updatedAt: string
}

export type StateSignUp = {
    errors?: {
        email?: string[];
        password?: string[];
        name?: string[];
        phone?: string[];
    };
    message?: string | null;
};

export interface PaginatedResponse<T> {
    items: T[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
}

export interface Category {
    id: string
    name: string
    description?: string
    createdAt?: string
    updatedAt?: string
}

export interface UploadState {
    progress: number
    error?: string
    success?: boolean
}



/**
 * Represents an order or group event for a customer
 * Maps to CustomerAppointmentDTO from the backend
 */
export interface OrderOfCustomer {
    id: string;                     // orderId
    eventScheduleId: string | null; // Required but can be null for individual sessions
    scheduledStart: string;         // ISO string
    scheduledEnd: string;           // ISO string
    customerId: string;
    guideId: string;
    guideGivenName: string;
    guideFamilyName: string;
    customerMessage: string | null; // Required but can be null for group sessions
    price: string;                  // Keeping as string for consistency with OrderOfGuide
    orderStatus: OrderStatus;
    productId: string;
    productName: string;
    productDescription: string;
    productPicture: string | null;  // Product image URL, null when not available
    productType: ProductType;
    sessionType: SessionType;
    maxParticipants: number | null; // Required but can be null for individual sessions
    currentParticipants: number | null; // Required but can be null for individual sessions
}

export interface OrderOfGuide {
    id: string;
    eventScheduleId: string;
    scheduledStart: string;
    scheduledEnd: string;
    customerId: string;
    guideId: string;
    anonymousName?: string;
    customerGivenName: string;
    customerFamilyName: string;
    customerMessage: string;
    customerPicture?: string;  // Optional as it might not always be present
    price: string;
    orderStatus: 'PAID' | 'COMPLETED' | 'CANCELLED' | 'RESERVED';
    productId: string;
    productName: string;
    productDescription: string;  // Fixed typo
    productType: 'CALL' | 'CHAT' | 'VIDEOCALL';  // Strict enum type
    sessionType: 'INDIVIDUAL' | 'GROUP';  // Strict enum type
    maxParticipants?: number;  // Optional for individual sessions
    currentParticipants?: number;  // Optional for individual sessions
    participants: Array<{
        orderId: string;
        customerId: string;
        customerGivenName: string;
        customerMessage: string;
    }>;
}

export interface BaseVideoDetails {
    videoToken: string;
    channel: string;
    guideId: string;
    guideName: string;
    guideGivenName: string;
    guidePicture: string;
    customerSecretName: string;
    productType: string;
    sessionType: string;
}

export interface GroupVideoDetails extends BaseVideoDetails {
    scheduleStart: string;
    scheduleEnd: string;
    maxParticipants: number;
    sessionType: 'GROUP';
}

export interface IndividualVideoDetails extends BaseVideoDetails {
    scheduledStart: string;
    scheduledEnd: string;
    sessionType: 'INDIVIDUAL';
}

// For backward compatibility
export const getVideoToken = getGroupVideoToken;


// relevant chat informations
export interface ExtendedOrder {
    // Existing Order fields
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    customerId: string;
    guideId: string;
    guideGivenName: string;
    guideFamilyName: string;
    price: string;
    orderStatus: 'PAID' | 'COMPLETED' | 'CANCELLED' | 'RESERVED';
    productId: string;
    productName: string;
    productDescription: string;
    productType: string;
    conversationSid?: string;
    chatToken?: string;
    callToken?: string;
    customerName?: string;
    customerSecretName?: string;
    customerGivenName?: string;
    customerFamilyName?: string;
    customerPicture?: string;
    guidePicture?: string;
    duration?: number; // in minutes
    isCustomer?: boolean;
}

export interface SearchResult {
    id: string;
    productId?: string;  // optional since it might be guide-only
    guideId: string;
    productName?: string;
    productDescription?: string;
    categoryIds: string[];
    categoryNames: string[];
    categoryDescriptions: string[];
    guideGivenName: string;
    guideFamilyName: string;
    resultType: string;  // "GUIDE" or "PRODUCT"
    combinedSearchText: string;
};

// app/types.ts
export interface Guide {
    id: string;
    given_name: string;
    family_name: string;
    fullName: string;
    picture?: string;
    email: string;
    badges?: GuideBadgeKey[];
    telephoneNumber?: string;
    smsNotificationsEnabled?: boolean;
    active: boolean;
    bank_account_name?: string;
    iban: string;
    city: string;
    bio: string;
    streetAndNumber: string;
    postalCode: string;
    country: string;
    website_url: string | null;
    instagram_url: string | null;
    facebook_url: string | null;
    twitter_url: string | null;
    linkedin_url: string | null;
}
// includes products
export interface GuideProfileData {
    guide: {
        id: string;
        given_name: string;
        family_name: string;
        fullName: string;
        picture?: string;
        email: string;
        city: string;
        bio: string;
        badges?: GuideBadgeKey[];
        streetAndNumber: string;
        postalCode: string;
        country: string;
        website_url?: string | null;
        instagram_url?: string | null;
        facebook_url?: string | null;
        twitter_url?: string | null;
        linkedin_url?: string | null;
    };
    products: UnifiedProductDisplay[];
}

export interface Customer {
    id: string;
    given_name: string;
    family_name: string;
    email: string;
    picture?: string | null;
    secretName?: string;
    telephoneNumber?: string;
    streetAndNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
}
/**
 * Valid country codes for DACH region
 */
export type CountryCode = 'DE' | 'AT' | 'CH';

export interface PriceOption {
    duration: number; // in minutes
    price: number; // in euros
}

export interface ConsultationType {
    type: 'chat' | 'call';
    options: PriceOption[];
}

export interface GuideBookingData {
    id: string;
    guideName: string;
    picture: string;
    badges?: GuideBadgeKey[];
    consultationTypes: ConsultationType[];
}

export interface GuideCardProps {
    id: string;
    imageUrl: string;
    title: string;
    description: string;
    guideName: string;
    profileImage: string;
    category: string;
    badges?: GuideBadgeKey[];
    consultationTypes: ConsultationType[];
}


// Guides Availability
export interface TimeSlot {
    start: string;
    end: string;
}

export interface Exclusion {
    startDate: Date;
    endDate: Date;
    slots: TimeSlot[];
}

export interface UpdateOrderResult {
    message: string;
    order: RescheduledOrder;
}

// revenue projections
export interface GuideEarningOrder {
    id: string;
    appointmentDate: string;  // ISO date string
    customerName: string;     // Customer name (anonymized as needed)
    productName: string;
    productType: ProductType;
    totalPrice: number;       // Original price
    guideEarnings: number;    // Guide's portion
    status: OrderStatus;      // PAID or COMPLETED
    creditNoteId?: string;    // Reference to credit note if processed
}

// Keep the existing GuideEarningsSummary with fixed properties
export interface GuideEarningsSummary {
    currentMonthOpenBookings: number;       // Total price of all outstanding/open appointments for current month
    currentMonthCompletedTurnover: number;  // Total turnover generated from completed appointments this month
    currentMonthCompletedMargins: number;   // Guide's margins from completed appointments this month
}

export interface MonthlyRevenueData {
    year: number;
    month: number;
    amount: number;
    percentageChange: number | null;
    dailyRevenue: Array<{
        date: string;
        amount: number;
        orderCount: number;
        completedOrderCount: number;
        futureOrderCount: number;
    }>;
}

export interface AnnualMonthlyRevenueData {
    year: number;
    month: number;
    amount: number;
    completedAmount: number;
    futureAmount: number;
    orderCount: number;
    completedOrderCount: number;
    futureOrderCount: number;
    percentageChange: number | null;
}

// Complete annual revenue response
export interface AnnualRevenueResponseData {
    yearlyRevenue: AnnualMonthlyRevenueData[];
    totalAmount: number;
    totalCompletedAmount: number;
    totalFutureAmount: number;
    yearOverYearGrowth: number | null;
}

export interface YearlyRevenueData {
    year: number;
    amount: number;
    percentageChange: number | null;
    monthlyRevenue: MonthlyRevenueData[];
}

export interface RescheduledOrder {
    orderId: string;
    customerId: string;
    guideId: string;
    productId: string;
    scheduledStart: string;
    scheduledEnd: string;
    status: 'PAID' | 'COMPLETED' | 'CANCELLED' | 'RESERVED';
    customerMessage: string;
    provisionId: string;
    paymentConfirmedAt: string;
    reservationExpiresAt: string;
    stripeSessionId: string;
    readyParticipants: string[];
    rescheduledAt: string;
    rescheduledBy: string;
}



export interface DayAvailability {
    slots: TimeSlot[];
}

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';


export type WeeklyAvailability = {
    [key in DayOfWeek]: DayAvailability;
};


export interface AvailableDate {
    date: string;
    slots: TimeSlot[];
    timezone: string;
}

// Products

export interface BaseProduct {
    id?: string;
    name: string;
    guideId: string;
    description: string;
    category_id: string[];
    duration: number;
    language?: string;
    teaser_img?: string;
    featured: boolean;
    sessionType: SessionType;
}

// Legacy product type (maintain backwards compatibility)
export interface Product extends BaseProduct {
    type: ProductType;
    price: string;
}

export interface GroupProduct extends BaseProduct {
    type: 'VIDEOCALL';
    schedules: EventSchedule[];
    price: string;
}


/**
 * Represents the availability status for group event slots
 */
export interface GuideGroupEventAvailabilityDTO {
    guideId: string;
    blockedDays: GroupEventDayAvailability[];
}

/**
 * Represents availability information for a specific day
 */
export interface GroupEventDayAvailability {
    date: string; // ISO string
    blockedSlots: BlockedTimeslot[];
}

/**
 * Represents a blocked time slot
 */
export interface BlockedTimeslot {
    start: string; // ISO string
    end: string; // ISO string
    type: string;
}


export interface ProductsByType {
    CHAT: Product[];
    CALL: Product[];
    VIDEOCALL: GroupProduct[];
}

// Add to app/api/types.ts

export interface GroupProductToSend {
    name: string;
    description: string;
    type: ProductType;
    sessionType: string;
    categoryIds: string[];
    teaserImg: string;
    language: string | null;
    featured: boolean;
    schedules?: Array<{
        startTime: string;
        endTime: string;
        maxParticipants: number;
        pricePerSeat: number;
    }>;
}

export interface GroupEventProduct extends Omit<Product, 'price' | 'sessionType' | 'maxParticipants'> {
    sessionType: 'GROUP';
    maxParticipants: number;
    schedules: EventSchedule[];
}

// for displaying groupevents on the homepage
export interface GroupEventDisplayProduct extends GroupEventProduct {
    guide: {
        id: string;
        name: string;
        profileImage: string | null;
    };
    categories: {
        id: string;
        name: string;
    }[];
}




export type ProductType = 'CHAT' | 'CALL' | 'VIDEOCALL';
export type SessionType = 'INDIVIDUAL' | 'GROUP' | 'STREAM';
export type ProductCreateInput = Omit<Product, 'id'>;
export type ProductCategory = string;
export type ProductUpdateInput = Partial<ProductCreateInput>;

export interface ProductFilters {
    minPrice?: number;
    maxPrice?: number;
    communicationType?: string;
    categoryId?: string;
}

export interface KlaroConfig {
    version: number;
    elementID: string;
    styling: {
        theme: string[];
    };
    // Add other properties as needed
}

export interface Klaro {
    setup: (config: KlaroConfig) => void;
    // Add other known Klaro methods here
}

export interface GuideAppointmentsResponse {
    // Current appointment
    currentOrderId?: string;
    currentStart?: string;
    currentEnd?: string;
    currentPartnerName?: string;

    // Next appointment
    nextOrderId?: string;
    nextStart?: string;
    nextCustomerName?: string;
}


export interface SessionEndData {
    orderId: string;
    conversationId: string;
    initiatedBy: string;
    messageCount?: number;
    endTime: string;
}

// Invoices related types
export interface Invoice {
    id: string;
    invoiceNumber: string;
    date: string;
    amount: number;
    downloadUrl: string;
    productName: string | null;
    status: InvoiceStatus;
}

export enum InvoiceStatus {
    COMPLETED = "COMPLETED",
    PENDING = "PENDING",
    CANCELLED = "CANCELLED"
}

export interface InvoiceApiResponse {
    invoices: Invoice[];
    error?: string;
}

export interface CreditNoteItem {
    orderId: string;
    serviceDate: string;
    description: string;
    amount: number;
    vatRate: number;
    vatAmount: number;
}


export interface GuidePayoutSummary {
    guideId: string;             // Unique identifier for the guide
    guideName: string;           // Name of the guide
    payoutSchedule: PayoutSchedule; // Enum or object defining payout schedule
    lastPayoutDate?: string;     // Nullable field for the last payout date
    pendingAmount: number;       // Amount pending payout
    unsolvedCasesCount: number;  // Count of unsolved cases
    processedOrdersCount: number; // Count of processed orders
    creditNotesCount: number;    // Count of credit notes
    iban?: string | null;        // Nullable field for banking info
    vatId?: string;              // Nullable field for VAT ID
}

export interface AdminPayoutSummary {
    period: string;              // Reporting period
    totalGuides: number;         // Total number of guides
    totalAmount: number;         // Total payout amount
    totalVat: number;            // Total VAT amount
    creditNoteCount: number;     // Count of credit notes
    readyForPayoutCount: number; // Count of payouts ready for processing
    unsolvedCasesCount: number;  // Count of unsolved cases
    guidesSummary: GuidePayoutSummary[]; // List of guide summaries
}

export interface UnsolvedCaseDTO {
    orderId: string;
    guideId: string;
    guideName: string;
    orderDate: string;
    amount: number;
    status: string;
    reason: string | null;
}
export type UnsolvedCasesDTO = UnsolvedCaseDTO[];

export enum PayoutSchedule {
    WEEKLY = "WEEKLY",
    MONTHLY = "MONTHLY",
    BIWEEKLY = "BIWEEKLY",
}


/**
 * Main interface containing all dashboard financial metrics
 */
export interface FinancialDashboardDTO {
    yearToDate: YearToDateMetricsDTO;
    monthly: Record<number, MonthlyMetricsDTO>;
}

/**
 * Year-to-date metrics broken down by revenue stages
 */
export interface YearToDateMetricsDTO {
    // Total revenue including VAT (Brutto)
    grossRevenue: MetricsSummaryDTO;
    // Revenue after VAT deduction (Netto)
    netRevenue: MetricsSummaryDTO;
    // Platform's share (25% of net revenue)
    platformRevenue: MetricsSummaryDTO;
    // Guide payments (75% of net revenue)
    guidePayouts: GuidePayoutMetricsDTO;
}

/**
 * Monthly breakdown of all financial metrics
 */
export interface MonthlyMetricsDTO {
    /** Revenue including VAT in cents */
    grossRevenue: MetricsSummaryDTO;
    /** Revenue after VAT in cents */
    netRevenue: MetricsSummaryDTO;
    /** Platform's share (25%) in cents */
    platformRevenue: MetricsSummaryDTO;
    /** Guide payouts (75%) with status in cents */
    guidePayouts: GuidePayoutMetricsDTO;
}
/** Amount in cents (1€ = 100 cents) */
type CentAmount = number;
/**
 * Core metrics used across different summaries
 */
export interface MetricsSummaryDTO {
    /** Total amount in cents */
    amount: CentAmount;
    /** Number of orders in this period */
    orderCount: number;
    /** Average value per order in cents */
    averageOrderValue: CentAmount;
}

/**
 * Extended metrics for guide payouts including status tracking
 */
export interface GuidePayoutMetricsDTO extends MetricsSummaryDTO {
    /** Amount pending payout in cents */
    pendingAmount: CentAmount;
    /** Amount already paid out in cents */
    completedAmount: CentAmount;
}

export interface PaymentMetricsDTO {
    pending: PaymentStatusMetricsDTO;
    processing: PaymentStatusMetricsDTO;
    completed: PaymentStatusMetricsDTO;
    failed: PaymentStatusMetricsDTO;
}

export interface PaymentStatusMetricsDTO {
    amount: number;              // in cents
    orderCount: number;
    averageProcessingTime: number | null;  // in minutes
}


export interface FailureReasonCount {
    reason: string;
    count: number;
}

export interface VerificationBreakdownDTO {
    status: string;
    count: number;
    commonFailureReasons: FailureReasonCount[];
}

export interface GuideProblematicOrdersDTO {
    guideId: string;
    guideName: string;
    orderCount: number;
    statuses: Record<string, number>;
    verificationBreakdown: VerificationBreakdownDTO[];
}

export interface ProblematicOrderSummaryDTO {
    totalOrders: number;
    byStatus: Record<string, number>;
    byVerificationStatus: Record<string, number>;
    byGuide: GuideProblematicOrdersDTO[];
    period: {
        startDate: string;
        endDate: string;
    };
}

export interface FormState {
    reason: string;
    notes: string;
    status: string;
}

export type PayoutStatus = 'READY_TO_COLLECT' | 'PENDING' | 'INITIATED' | 'COMPLETED' | 'FAILED'

export interface PayoutOrder {
    orderId: string
    customerName: string
    date: string               // completion date
    netAmount: number
    platformFee: number
    payoutAmount: number
    creditNoteId: string
    creditNoteNumber?: string
}

export interface CreditNoteOverview {
    id: string
    creditNoteNumber?: string
    status: PayoutStatus
    amount: number
    createdAt: string
    updatedAt: string
    errorMessage?: string
}

export interface GuidePayoutListItem {
    guideId: string
    guideName: string
    iban: string
    completedOrderCount: number
    netAmount: number
    payoutAmount: number
    // New fields
    amountByStatus: Record<PayoutStatus, number>
    creditNotes: CreditNoteOverview[]
    orders: PayoutOrder[]
}

export interface PayoutSummary {
    totalPendingPayouts: number;
    guidesWithPendingPayouts: number;
    guidePayouts: GuidePayoutListItem[];
    totalsByStatus: Record<PayoutStatus, number>;
}

// Enhanced CreditNote interface
export interface CreditNote {
    id: string
    guideId: string
    guideName: string
    creditNoteNumber?: string
    status: PayoutStatus
    amount: number
    vatAmount: number
    totalAmount: number
    updatedAt?: string
    periodStart: string
    periodEnd: string
    createdAt: string
    paidAt?: string
    pdfUrl?: string
    errorMessage?: string
    bankDetails: {
        iban: string
        vatId?: string
    }
    orders: Array<{
        orderId: string
        customerName: string    // Added for better display
        amount: number
        orderDate: string
        productTitle?: string   // Added for better context
        netAmount: number       // Added to match PayoutOrder
        platformFee: number     // Added to match PayoutOrder
        payoutAmount: number    // Added to match PayoutOrder
    }>
}

// Updated summary to handle all status types
export interface CreditNotesSummary {
    totalsByStatus: Record<PayoutStatus, {
        count: number
        amount: number
    }>
    creditNotesByStatus: Record<PayoutStatus, CreditNote[]>
}

// Helper type for status grouping in UI
export interface StatusGroup {
    status: PayoutStatus
    notes: CreditNote[]
    totalAmount: number
    count: number
}

export interface ProblematicOrder {
    orderId: string;
    guideId: string;
    scheduledStart: string;  // LocalDateTime comes as ISO string
    scheduledEnd: string;    // LocalDateTime comes as ISO string
    status: OrderStatus;
    guideName: string;
    productName: string;
    price: number;          // Double comes as number in JS
    isResolved: boolean;
    unresolvedReason?: string | null;
    resolutionNotes?: string | null;
    resolvedAt?: string | null;      // Instant comes as ISO string
    sessionStartTime?: string | null; // LocalDateTime comes as ISO string
    sessionEndTime?: string | null;   // LocalDateTime comes as ISO string
    sessionDuration?: string | null;
    participationVerification?: ParticipationVerificationData | null;
}

export enum VerificationStatus {
    VERIFIED = 'VERIFIED',
    // Chat-specific failures
    FAILED_NO_MESSAGES = 'FAILED_NO_MESSAGES',
    FAILED_ONE_SIDED_CONVERSATION = 'FAILED_ONE_SIDED_CONVERSATION',
    FAILED_INACTIVE_PERIOD = 'FAILED_INACTIVE_PERIOD',
    // Call-specific failures
    FAILED_NO_CALL = 'FAILED_NO_CALL',
    FAILED_TOO_SHORT = 'FAILED_TOO_SHORT',
    FAILED_NO_SHOW = 'FAILED_NO_SHOW'
}

export interface ParticipationVerificationData {
    verifiedAt: string;  // LocalDateTime comes as ISO string
    hasMessages: boolean;
    participants: string[];  // Set<String> comes as string array in JSON
    messageCount: number;
    verificationStatus: VerificationStatus;
    failureReason?: string | null;
}

export enum OrderStatus {
    RESERVED = 'RESERVED',
    PENDING_PAYMENT = 'PENDING_PAYMENT',
    PAID = 'PAID',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED',
    TIMED_OUT = 'TIMED_OUT',
    NO_SHOW = 'NO_SHOW',
    UNRESOLVED = 'UNRESOLVED'
}

export interface AdminOrderResolutionRequest {
    reason: string;
    status: OrderStatus;  // New field
    notes?: string | null;
    adminId?: string;
    timestamp?: string;
}